import { Button } from '@davidlloydleisure/common-js-frontend';
import type { CSSInterpolation } from '@emotion/serialize';
import classNames from 'classnames';
import React, { forwardRef, use } from 'react';

import { isAbbyChatLink } from 'src/components/common/abby-chat/abby-chat-link';
import { WebsiteLink } from 'src/components/common/website-link';
import { WebsiteLinkButtonContent } from 'src/components/common/website-link-button-content';
import { SiteContext } from 'src/context/site-context';
import type { RedesignButtonStyle } from 'src/enums/button-style';
import abby from 'src/helpers/abby';
import { getBrandForBuildType } from 'src/helpers/brand-for-build-type';
import { buttonStyles } from 'src/helpers/button-styles';
import type { StaticTextLookupKey } from 'src/translators/static-text-lookup';
import { useStaticTextTranslator } from 'src/translators/static-text-translator';

import * as styles from './link-button-content.module.scss';

type GlobalLinkButtonContentProps = {
  buttonText?: string | null;
  buttonKey?: StaticTextLookupKey;
  buttonLink?: string | null;
  buttonCss?: CSSInterpolation;
  buttonEndIcon?: React.ReactElement;
  buttonType?: string;
  gtmTrackingId?: string;
  className?: string;
  contentClassName?: string;
  buttonStyle?: Queries.ButtonStyle | null;
  isRedesignButton?: boolean;
  openInNewTab?: boolean;
  forceOpenInSameTab?: boolean;
  handleClick?: () => void;
};

const GlobalLinkButtonContent = forwardRef<HTMLElement, GlobalLinkButtonContentProps>(
  (
    {
      buttonText,
      buttonKey,
      buttonLink,
      buttonCss,
      buttonEndIcon,
      buttonType,
      gtmTrackingId,
      className = '',
      contentClassName,
      buttonStyle,
      isRedesignButton,
      openInNewTab,
      forceOpenInSameTab,
      handleClick,
    },
    ref
  ) => {
    const translate = useStaticTextTranslator();
    const { buildType } = use(SiteContext);
    const tabIndexProp = isRedesignButton ? { tabIndex: 0 } : { tabIndex: 0 };
    const brand = getBrandForBuildType(buildType);

    const content = (
      <WebsiteLinkButtonContent
        buttonCss={buttonCss}
        buttonKey={buttonKey}
        buttonText={buttonText}
        buttonEndIcon={buttonEndIcon}
        buttonType={buttonType}
        contentClassName={contentClassName}
        gtmTrackingId={gtmTrackingId}
      />
    );

    if (isAbbyChatLink(buttonLink)) {
      return (
        <Button
          brand={brand}
          onClick={() => {
            abby.open();
            handleClick?.();
          }}
          text={buttonText ? buttonText : translate(buttonKey!)}
          style={buttonStyles(buttonStyle as RedesignButtonStyle)}
          focusBorderClassName={classNames(className, 'abby-chat-link')}
          endIcon={buttonEndIcon}
          className={className}
        />
      );
    }

    const linkClassNames = classNames(
      isRedesignButton && [
        styles.redesignButton,
        buttonStyle === 'orange' && styles.orange,
        buttonStyle === 'blue' && styles.blue,
        buttonStyle === 'transparent_with_white_font_and_border' &&
          styles.transparentWithWhiteFontAndBorder,
        buttonStyle === 'transparent_with_blue_font_and_border' &&
          styles.transparentWithBlueFontAndBorder,
        buttonStyle === 'transparent_with_grey_font_and_border' &&
          styles.transparentWithGreyFontAndBorder,
        buttonStyle === 'white_with_harbour_grey_font' && styles.whiteWithHarbourGreyFont,
        buttonStyle === 'harbour_grey_with_white_font' && styles.harbourGreyWithWhiteFont,
        buttonStyle === 'transparent_with_gold_font_and_border' &&
          styles.transparentWithGoldFontAndBorder,
        buttonStyle === 'transparent' && styles.transparent,
        buttonStyle === 'white_with_plum_font' && styles.whiteWithPlumFont,
        buttonStyle === 'white_with_green_font' && styles.whiteWithGreenFont,
        buttonStyle === 'transparent_with_white_font' && styles.transparentWithWhiteFont,
        buttonStyle === 'plum' && styles.plum,
        buttonStyle === 'dark_green' && styles.green,
      ],
      className
    );

    if (
      buttonLink &&
      (buttonLink.includes('http') || buttonLink.includes('localhost') || openInNewTab)
    ) {
      return (
        <a
          target={forceOpenInSameTab ? '_self' : '_blank'}
          rel={forceOpenInSameTab ? undefined : 'noopener noreferrer'}
          href={buttonLink}
          ref={ref as React.ForwardedRef<HTMLAnchorElement>}
          className={linkClassNames}
          onClick={() => handleClick?.()}
          {...tabIndexProp}
        >
          {content}
        </a>
      );
    }

    return (
      <WebsiteLink
        to={buttonLink || '/'}
        goToGlobal={true}
        className={linkClassNames}
        isTabIndex={isRedesignButton}
        forceOpenInSameTab={forceOpenInSameTab}
        handleClick={handleClick}
        ref={ref}
      >
        {content}
      </WebsiteLink>
    );
  }
);

GlobalLinkButtonContent.displayName = 'GlobalLinkButtonContent';

export default GlobalLinkButtonContent;
